import { useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';
import { getPriceCut } from 'components/marketplace/helpers/certaintyPanel';
import { getDisplayPrice } from 'components/marketplace/helpers/displayPrice';
import { AVAILABLE_SOON_DISCLOSURE } from 'components/marketplace/shared/availableSoon';
import { isPropertyAvailableForPurchase } from 'components/properties/helpers/property';
import { useTouring } from 'components/self-tour/hooks/useTouring';
import type { ChosenSlot } from 'components/tours/TourBookingSteps';

import { BuyDirectContextModal } from '../PropertySection/BuyDirectContextModal';
import {
  SelfTourDialogSurface,
  SelfTourQueryClientProvider,
} from '../PropertySection/SelfTourDialog';
import { TourWithAgentModal } from '../PropertySection/TourWithAgentModal';
import type { MarketplaceProperty } from '../types';
import { PdpAgentTourCard } from './PdpAgentTourCard';
import { PdpPurchaseCard } from './PdpPurchaseCard';
import { TourActionButton } from './PdpTourActionButton';
import { PdpTourCard } from './PdpTourCard';

export const PdpPriceCard = ({ property }: { property: MarketplaceProperty }) => {
  const touring = useTouring(property, { placement: 'main' });
  const [initialTourSlot, setInitialTourSlot] = useState<ChosenSlot>();
  const openTour = (slot?: ChosenSlot) => {
    setInitialTourSlot(slot);
    (touring?.secondaryAction ?? touring?.primaryAction)?.onClick?.();
  };
  const quotesBuyDirect = property.listingState === 'ON_THE_MARKET' && touring?.showBuyDirect;
  const price = quotesBuyDirect
    ? (property.buyDirectPrice ?? property.listPrice)
    : getDisplayPrice(property, 'listed');
  const monthlyPayment = quotesBuyDirect
    ? (property.buyDirectEstimatedMonthlyPayment ?? property.estimatedMonthlyPayment)
    : property.estimatedMonthlyPayment;
  let priceLabel = 'Price not available';
  if (price != null) {
    priceLabel = formatCurrency(price);
  } else if (property.listingState === 'AVAILABLE_SOON') {
    priceLabel = 'Price pending';
  }
  const availableForPurchase = isPropertyAvailableForPurchase(property);
  const priceCut = availableForPurchase ? getPriceCut(property.listingHistory) : null;
  const savings = availableForPurchase && touring?.showBuyDirect ? property.buyDirectSavings : null;
  const showSavings = savings != null && savings > 0;

  return (
    <SelfTourQueryClientProvider>
      <Col className="w-full gap-5">
        {touring?.tourKind === 'tourWithAgent' && (
          <PdpAgentTourCard action={touring.secondaryAction ?? touring.primaryAction} />
        )}
        {quotesBuyDirect && touring ? (
          <>
            {touring.tourKind === 'selfTour' && (
              <PdpTourCard listingId={property.tourBookingListingId} onChoose={openTour} />
            )}
            <PdpPurchaseCard property={property} touring={touring} />
          </>
        ) : (
          <Col
            as="section"
            aria-label="Home price"
            className="relative w-full overflow-hidden rounded-xl"
          >
            <Col className="relative w-full rounded-xl bg-od-bg-secondary">
              <Col className="@container relative w-full gap-7 rounded-xl border border-od-border-primary bg-od-bg-primary p-5 lg:p-7">
                <Box className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
                  <Col className="min-w-0 gap-3">
                    {showSavings && (
                      <Text className="text-label-sm text-od-content-accent-brand">
                        Save {formatCurrency(savings)}
                      </Text>
                    )}
                    {!showSavings && priceCut && (
                      <Text className="text-label-sm text-od-content-accent-brand">
                        {formatCurrency(priceCut.cutCents / 100)} price drop
                      </Text>
                    )}
                    <Text as="h2" className="text-header-sm text-od-content-primary">
                      {priceLabel}
                    </Text>
                    {showSavings && priceCut && (
                      <Text className="text-body-sm text-od-content-secondary">
                        {formatCurrency(priceCut.cutCents / 100)} price drop
                      </Text>
                    )}
                  </Col>
                  {availableForPurchase && monthlyPayment != null && (
                    <Col className="max-w-32 items-end gap-3 text-right @[24rem]:max-w-none">
                      <Text className="text-label-sm text-od-content-secondary">
                        Est. Monthly Payment
                      </Text>
                      <Text className="text-label-xl text-od-content-primary">
                        {formatCurrency(monthlyPayment)}
                      </Text>
                    </Col>
                  )}
                </Box>
                {property.listingState === 'AVAILABLE_SOON' && (
                  <Text as="p" className="text-body-sm text-od-content-tertiary">
                    {AVAILABLE_SOON_DISCLOSURE}
                  </Text>
                )}
                {touring && touring.tourKind !== 'tourWithAgent' && (
                  <Box className={touring.secondaryAction ? 'grid grid-cols-2 gap-3' : 'w-full'}>
                    {touring.secondaryAction && (
                      <TourActionButton
                        action={touring.secondaryAction}
                        variant={
                          touring.tourKind === 'selfTour'
                            ? 'primary'
                            : touring.secondaryAction.variant
                        }
                        label={touring.tourKind === 'selfTour' ? 'Take a self tour' : undefined}
                      />
                    )}
                    <TourActionButton
                      action={touring.primaryAction}
                      variant={touring.showBuyDirect ? 'secondary' : touring.primaryAction.variant}
                      label={
                        !touring.showBuyDirect && touring.tourKind === 'selfTour'
                          ? 'Take a self tour'
                          : undefined
                      }
                    />
                  </Box>
                )}
              </Col>
            </Col>
          </Col>
        )}
      </Col>
      {touring && (
        <>
          {touring.tourKind === 'tourWithAgent' && (
            <TourWithAgentModal
              property={property}
              isOpen={touring.isDialogOpen}
              onClose={touring.closeDialog}
            />
          )}
          <SelfTourDialogSurface
            initialSlot={initialTourSlot}
            isOpen={touring.isSelfTourDialogOpen}
            onClose={touring.closeSelfTourDialog}
            onOpenRequest={touring.openSelfTourDialog}
            placement="main"
            addressToken={property.addressToken}
            slug={property.slug}
            tourBookingListingId={property.tourBookingListingId}
            addressLine={[property.street, property.city].filter(Boolean).join(', ')}
          />
          <BuyDirectContextModal
            isOpen={touring.isBuyDirectContextOpen}
            onClose={touring.closeBuyDirectContext}
            continueHref={touring.buyDirectContextHref}
          />
        </>
      )}
    </SelfTourQueryClientProvider>
  );
};
